import React, { useRef, useState } from 'react';
import { revenueSeries, money } from '../data.js';

const RANGES = { '1D': 2, '1W': 3, '1M': 4, '6M': 6, '1Y': 12, ALL: 12 };
const W = 600, H = 220, PAD = 10;

export default function RevenueChart() {
  const [range, setRange] = useState('1Y');
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const series = revenueSeries.slice(-RANGES[range]);
  const max = Math.max(...series.map((d) => d.value));
  const min = Math.min(...series.map((d) => d.value)) * 0.85;
  const range_ = max - min || 1;
  const stepX = series.length > 1 ? (W - PAD * 2) / (series.length - 1) : 0;
  const yFor = (v) => H - PAD - ((v - min) / range_) * (H - PAD * 2);
  const points = series.map((d, i) => ({ ...d, x: PAD + i * stepX, y: yFor(d.value) }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x},${H} L${points[0].x},${H} Z`;

  const total = series.reduce((s, d) => s + d.value, 0);
  const prevSlice = revenueSeries.slice(-RANGES[range] * 2, -RANGES[range]);
  const prevTotal = prevSlice.reduce((s, d) => s + d.value, 0);
  const delta = prevSlice.length ? Math.round(((total - prevTotal) / prevTotal) * 100) : null;

  const onMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    let idx = 0, best = Infinity;
    points.forEach((p, i) => { const d = Math.abs(p.x - relX); if (d < best) { best = d; idx = i; } });
    setHover(idx);
  };

  const hp = hover != null ? points[hover] : null;

  return (
    <div className="card">
      <div className="card-body">
        <div className="revenue-head">
          <div>
            <div className="card-title">Revenue overview</div>
            <div style={{ display: 'flex', alignItems: 'baseline', marginTop: 6 }}>
              <span className="revenue-value">{money(total)}</span>
              {delta != null && <span className="revenue-delta">{delta >= 0 ? '+' : ''}{delta}% vs prior period</span>}
            </div>
          </div>
          <div className="chart-range-tabs">
            {Object.keys(RANGES).map((r) => (
              <span key={r} className={`chart-range-tab ${range === r ? 'active' : ''}`} onClick={() => { setRange(r); setHover(null); }}>{r}</span>
            ))}
          </div>
        </div>
        <div className="chart-wrap" onMouseLeave={() => setHover(null)}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 220, display: 'block' }} onMouseMove={onMove}>
            <defs>
              <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#2B5CE6" stopOpacity=".22" />
                <stop offset="1" stopColor="#2B5CE6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <g opacity="0.5">
              {[0.25, 0.5, 0.75, 1].map((f) => <line key={f} x1="0" y1={H * f} x2={W} y2={H * f} stroke="var(--border)" strokeWidth="1" />)}
            </g>
            <path d={areaPath} fill="url(#revFill)" />
            <path d={linePath} fill="none" stroke="#2B5CE6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {hp && <line x1={hp.x} y1="0" x2={hp.x} y2={H} stroke="var(--text-faint)" strokeWidth="1" strokeDasharray="4 4" />}
            {hp && <circle cx={hp.x} cy={hp.y} r="4.5" fill="#2B5CE6" stroke="#fff" strokeWidth="2" />}
          </svg>
          {hp && (
            <div className="chart-tooltip" style={{ left: `${(hp.x / W) * 100}%`, top: `${(hp.y / H) * 100}%` }}>
              <div className="tt-date">{hp.month} {hp.year}</div>
              <div className="tt-value">{money(hp.value)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
